import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Trash2, ShoppingCart } from "lucide-react";
import { SmartbouffeLogoSmall } from "../components/Logo";
import { Footer } from "../components/Footer";
import AdBanner from '../components/AdBanner';
import { MealIcon, CheckIcon, BreakfastIcon, CalendarIcon, StarIcon, ForkKnifeIcon, VegetarianIcon } from '../components/Icons';

export default function Menu() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [mealPlan, setMealPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog pour ajouter une recette
  const [showAddMealDialog, setShowAddMealDialog] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedMealType, setSelectedMealType] = useState(null);
  
  // Recherche de recettes
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  
  // Liste d'épicerie
  const [showGroceryList, setShowGroceryList] = useState(false);
  const [groceryList, setGroceryList] = useState([]);
  const [selectedRecipesForGrocery, setSelectedRecipesForGrocery] = useState([]);

  useEffect(() => {
    // Initialiser à la semaine courante (dimanche)
    const today = new Date();
    const sunday = getSunday(today);
    setCurrentWeekStart(sunday);
  }, []);

  useEffect(() => {
    if (currentWeekStart) {
      loadMealPlan();
      loadRecipes();
      loadCategories();
    }
  }, [currentWeekStart]); // loadMealPlan, loadRecipes, loadCategories are stable

  const parseDate = (dateInput) => {
    if (typeof dateInput === 'string') {
      // Parser une string au format YYYY-MM-DD en mode local
      const [year, month, day] = dateInput.split('-').map(Number);
      return new Date(year, month - 1, day, 12, 0, 0); // Midi pour éviter les problèmes
    }
    // Si c'est déjà une Date, retourner une copie
    return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate(), 12, 0, 0);
  };

  const formatDate = (date) => {
    const d = parseDate(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getSunday = (date) => {
    const d = parseDate(date);
    const dayOfWeek = d.getDay(); // 0 = dimanche, 1 = lundi, 2 = mardi, etc.
    
    // Calculer le dimanche de cette semaine
    const sunday = new Date(d);
    sunday.setDate(d.getDate() - dayOfWeek);
    
    return formatDate(sunday);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const sunday = getSunday(today);
    setCurrentWeekStart(sunday);
  };

  const loadMealPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/meal-plans/${currentWeekStart}`,
        { withCredentials: true }
      );
      setMealPlan(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du menu");
    } finally {
      setLoading(false);
    }
  };

  const loadRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`, { withCredentials: true });
      setRecipes(response.data);
      applyFilters(response.data, searchQuery, selectedCategoryFilter, selectedStatusFilter);
    } catch (error) {
      setRecipes([]);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      setCategories([]);
    }
  };

  const applyFilters = (recipeList, query, categoryFilter, statusFilter) => {
    let filtered = [...recipeList];

    // Filtre par recherche
    if (query && query.trim()) {
      const keyword = query.toLowerCase();
      filtered = filtered.filter(recipe => 
        recipe.title.toLowerCase().includes(keyword) ||
        (recipe.ingredients_quebec || []).some(ing => ing.toLowerCase().includes(keyword))
      );
    }

    // Filtre par catégorie
    if (categoryFilter) {
      filtered = filtered.filter(recipe => recipe.category_id === categoryFilter);
    }

    // Filtre par statut
    if (statusFilter) {
      filtered = filtered.filter(recipe => recipe.status === statusFilter);
    }

    setFilteredRecipes(filtered);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    applyFilters(recipes, query, selectedCategoryFilter, selectedStatusFilter);
  };

  const handleCategoryFilter = (categoryId) => {
    setSelectedCategoryFilter(categoryId);
    applyFilters(recipes, searchQuery, categoryId, selectedStatusFilter);
  };

  const handleStatusFilter = (status) => {
    setSelectedStatusFilter(status);
    applyFilters(recipes, searchQuery, selectedCategoryFilter, status);
  };

  const navigateWeek = (direction) => {
    const currentDate = parseDate(currentWeekStart);
    currentDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentWeekStart(formatDate(currentDate));
  };

  const getWeekDates = () => {
    if (!currentWeekStart) return [];
    
    const start = parseDate(currentWeekStart);
    const dates = [];
    const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push({
        name: dayNames[i],
        key: dayKeys[i],
        date: date.getDate(),
        month: date.toLocaleDateString('fr-CA', { month: 'short' }),
        fullDate: formatDate(date)
      });
    }
    return dates;
  };

  const handleAddMeal = (day, mealType) => {
    setSelectedDay(day);
    setSelectedMealType(mealType);
    setShowAddMealDialog(true);
  };

  const handleSelectRecipe = async (recipeId) => {
    try {
      await axios.post(
        `${API}/meal-plans/${currentWeekStart}/add-meal`,
        {
          recipe_id: recipeId,
          day: selectedDay,
          meal_type: selectedMealType
        },
        { withCredentials: true }
      );
      
      toast.success("Recette ajoutée au menu!");
      setShowAddMealDialog(false);
      setSearchQuery("");
      loadMealPlan();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleRemoveMeal = async (day, mealType, recipeId) => {
    try {
      await axios.delete(
        `${API}/meal-plans/${currentWeekStart}/remove-meal`,
        {
          data: { day, meal_type: mealType, recipe_id: recipeId },
          withCredentials: true
        }
      );
      
      toast.success("Recette retirée du menu");
      loadMealPlan();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleGenerateGroceryList = async () => {
    if (!mealPlan) return;
    
    // Collecter toutes les recettes de la semaine
    const allRecipeIds = new Set();
    Object.values(mealPlan.meals || {}).forEach(day => {
      ['dejeuner', 'diner', 'souper', 'extra'].forEach(mealType => {
        (day[mealType] || []).forEach(meal => {
          allRecipeIds.add(meal.recipe_id);
        });
      });
    });
    
    setSelectedRecipesForGrocery(Array.from(allRecipeIds));
    
    try {
      const response = await axios.post(
        `${API}/meal-plans/grocery-list`,
        {
          week_start_date: currentWeekStart,
          selected_recipe_ids: Array.from(allRecipeIds)
        },
        { withCredentials: true }
      );
      
      setGroceryList(response.data.ingredients);
      setShowGroceryList(true);
    } catch (error) {
      toast.error("Erreur lors de la génération de la liste");
    }
  };

  const toggleGroceryItem = (index) => {
    const newList = [...groceryList];
    newList[index].checked = !newList[index].checked;
    setGroceryList(newList);
  };

  const getMealIcon = (mealType) => {
    const icons = {
      dejeuner: <BreakfastIcon size={18} color="#D4A574" />,
      diner: <VegetarianIcon size={18} color="#87A96B" />,
      souper: <MealIcon size={18} color="#D4A574" />,
      extra: <StarIcon size={18} color="#FFC107" />
    };
    return icons[mealType] || <ForkKnifeIcon size={18} color="#D4A574" />;
  };

  const getMealLabel = (mealType) => {
    const labels = {
      dejeuner: 'Déjeuner',
      diner: 'Dîner',
      souper: 'Souper',
      extra: 'Extra'
    };
    return labels[mealType] || mealType;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p>Chargement...</p>
      </div>
    );
  }

  const weekDates = getWeekDates();
  const startDate = parseDate(currentWeekStart);
  const endDate = parseDate(currentWeekStart);
  endDate.setDate(endDate.getDate() + 6);
  
  return (
    <div style={{ minHeight: '100vh', background: '#FFF9E6', padding: '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <SmartbouffeLogoSmall />
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#8B7355',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontFamily: 'Playfair Display, serif'
          }}>
            <CalendarIcon size={28} color="#8B7355" /> Menu de la semaine
          </h1>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            style={{ borderColor: '#D4A574', color: '#8B7355' }}
          >
            <ArrowLeft size={18} style={{ marginRight: '0.5rem' }} />
            Retour
          </Button>
          <Button
            onClick={handleGenerateGroceryList}
            style={{ background: '#87A96B', color: 'white' }}
          >
            <ShoppingCart size={18} style={{ marginRight: '0.5rem' }} />
            Liste d'épicerie
          </Button>
        </div>
      </div>

      {/* Navigation de semaine */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        border: '2px solid #D4A574',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <Button
          variant="ghost"
          onClick={() => navigateWeek(-1)}
          style={{ color: '#8B7355', minWidth: '40px' }}
        >
          <ChevronLeft size={24} />
        </Button>
        
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: '1.3rem',
            fontWeight: '600',
            color: '#8B7355',
            marginBottom: '0.25rem'
          }}>
            {startDate.toLocaleDateString('fr-CA', { day: 'numeric', month: 'long' })} - {endDate.toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' })}
          </h2>
          <p style={{ color: '#A0826D', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Semaine du {startDate.toLocaleDateString('fr-CA')}
          </p>
          <Button
            variant="outline"
            onClick={goToCurrentWeek}
            style={{
              borderColor: '#87A96B',
              color: '#87A96B',
              fontSize: '0.8rem',
              padding: '0.35rem 0.75rem',
              marginTop: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
          >
            <CalendarIcon size={14} color="#87A96B" /> Cette semaine
          </Button>
        </div>
        
        <Button
          variant="ghost"
          onClick={() => navigateWeek(1)}
          style={{ color: '#8B7355', minWidth: '40px' }}
        >
          <ChevronRight size={24} />
        </Button>
      </div>

      {/* Calendrier - Cartes empilées */}
      <div style={{ display: 'grid', gap: '1.5rem' }}>
        {weekDates.map((day) => {
          const dayMeals = mealPlan?.meals?.[day.key] || {};
          
          return (
            <div key={day.key} style={{
              background: 'white',
              borderRadius: '12px',
              border: '3px solid #D4A574',
              padding: '1.5rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {/* En-tête du jour */}
              <div style={{
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                borderBottom: '2px solid #F5F1E8'
              }}>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#8B7355',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {day.name}
                </h3>
                <p style={{ color: '#A0826D', fontSize: '0.9rem' }}>
                  {day.date} {day.month}
                </p>
              </div>

              {/* Sections de repas */}
              <div style={{ display: 'grid', gap: '1rem' }}>
                {['dejeuner', 'diner', 'souper', 'extra'].map((mealType) => {
                  const meals = dayMeals[mealType] || [];
                  
                  return (
                    <div key={mealType} style={{
                      background: '#F5F1E8',
                      borderRadius: '8px',
                      padding: '1rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.75rem'
                      }}>
                        <h4 style={{
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#8B7355',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          {getMealIcon(mealType)} {getMealLabel(mealType)}
                        </h4>
                      </div>

                      {/* Liste des recettes */}
                      {meals.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {meals.map((meal) => (
                            <div
                              key={meal.recipe_id}
                              style={{
                                background: 'white',
                                padding: '0.75rem 1rem',
                                borderRadius: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                border: '1px solid #D4A574',
                                transition: 'all 0.2s'
                              }}
                              onClick={() => navigate(`/recipe/${meal.recipe_id}`)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#FFF9E6';
                                e.currentTarget.style.transform = 'translateX(4px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.transform = 'translateX(0)';
                              }}
                              title={meal.recipe_title}
                            >
                              <span style={{
                                color: '#3A4A3E',
                                fontWeight: '500',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1
                              }}>
                                {meal.recipe_title.length > 30 
                                  ? meal.recipe_title.substring(0, 30) + '...' 
                                  : meal.recipe_title}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveMeal(day.key, mealType, meal.recipe_id);
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#DC3545',
                                  cursor: 'pointer',
                                  padding: '0.25rem',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                title="Retirer"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}

                      {/* Bouton ajouter */}
                      {meals.length < 2 && (
                        <button
                          onClick={() => handleAddMeal(day.key, mealType)}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'white',
                            border: '2px dashed #D4A574',
                            borderRadius: '8px',
                            color: '#8B7355',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            fontWeight: '500',
                            transition: 'all 0.2s',
                            marginTop: meals.length > 0 ? '0.5rem' : '0'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#FFF9E6';
                            e.currentTarget.style.borderColor = '#87A96B';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.borderColor = '#D4A574';
                          }}
                        >
                          <Plus size={18} />
                          Ajouter une recette
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog Ajouter Recette */}
      <Dialog open={showAddMealDialog} onOpenChange={(open) => {
        setShowAddMealDialog(open);
        if (!open) {
          // Reset les filtres quand on ferme le dialog
          setSearchQuery("");
          setSelectedCategoryFilter(null);
          setSelectedStatusFilter(null);
          applyFilters(recipes, "", null, null);
        }
      }}>
        <DialogContent style={{ maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle>Choisir une recette</DialogTitle>
          </DialogHeader>
          
          {/* Barre de recherche */}
          <div style={{ marginBottom: '1rem' }}>
            <Input
              placeholder="Rechercher par nom ou ingrédient..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ border: '2px solid #D4A574' }}
            />
          </div>

          {/* Filtres */}
          <div style={{ marginBottom: '1rem' }}>
            {/* Filtre par catégorie */}
            <div style={{ marginBottom: '0.75rem' }}>
              <p style={{ 
                fontSize: '0.85rem', 
                fontWeight: '600', 
                color: '#8B7355', 
                marginBottom: '0.5rem' 
              }}>
                Catégorie:
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleCategoryFilter(null)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    border: selectedCategoryFilter === null ? '2px solid #87A96B' : '1px solid #D4A574',
                    background: selectedCategoryFilter === null ? '#87A96B' : 'white',
                    color: selectedCategoryFilter === null ? 'white' : '#8B7355',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: selectedCategoryFilter === null ? '600' : '400'
                  }}
                >
                  Toutes
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryFilter(cat.id)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '8px',
                      border: selectedCategoryFilter === cat.id ? '2px solid #87A96B' : '1px solid #D4A574',
                      background: selectedCategoryFilter === cat.id ? '#87A96B' : 'white',
                      color: selectedCategoryFilter === cat.id ? 'white' : '#8B7355',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: selectedCategoryFilter === cat.id ? '600' : '400'
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Filtre par statut */}
            <div>
              <p style={{ 
                fontSize: '0.85rem', 
                fontWeight: '600', 
                color: '#8B7355', 
                marginBottom: '0.5rem' 
              }}>
                Statut:
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleStatusFilter(null)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    border: selectedStatusFilter === null ? '2px solid #87A96B' : '1px solid #D4A574',
                    background: selectedStatusFilter === null ? '#87A96B' : 'white',
                    color: selectedStatusFilter === null ? 'white' : '#8B7355',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: selectedStatusFilter === null ? '600' : '400'
                  }}
                >
                  Tous
                </button>
                <button
                  onClick={() => handleStatusFilter('à_faire')}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    border: selectedStatusFilter === 'à_faire' ? '2px solid #2196F3' : '1px solid #D4A574',
                    background: selectedStatusFilter === 'à_faire' ? '#2196F3' : 'white',
                    color: selectedStatusFilter === 'à_faire' ? 'white' : '#8B7355',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: selectedStatusFilter === 'à_faire' ? '600' : '400'
                  }}
                >
                  🔵 À faire
                </button>
                <button
                  onClick={() => handleStatusFilter('fait')}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    border: selectedStatusFilter === 'fait' ? '2px solid #4CAF50' : '1px solid #D4A574',
                    background: selectedStatusFilter === 'fait' ? '#4CAF50' : 'white',
                    color: selectedStatusFilter === 'fait' ? 'white' : '#8B7355',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: selectedStatusFilter === 'fait' ? '600' : '400'
                  }}
                >
                  <CheckIcon size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                  Fait
                </button>
                <button
                  onClick={() => handleStatusFilter('à_refaire')}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    border: selectedStatusFilter === 'à_refaire' ? '2px solid #FFC107' : '1px solid #D4A574',
                    background: selectedStatusFilter === 'à_refaire' ? '#FFC107' : 'white',
                    color: selectedStatusFilter === 'à_refaire' ? 'white' : '#8B7355',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: selectedStatusFilter === 'à_refaire' ? '600' : '400',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <StarIcon size={14} color={selectedStatusFilter === 'à_refaire' ? 'white' : '#8B7355'} /> À refaire
                </button>
              </div>
            </div>
          </div>

          <div style={{ 
            borderTop: '1px solid #D4A574', 
            paddingTop: '1rem',
            marginBottom: '0.5rem'
          }}>
            <p style={{ fontSize: '0.85rem', color: '#A0826D' }}>
              {filteredRecipes.length} recette(s) affichée(s)
            </p>
          </div>

          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {filteredRecipes.length > 0 ? (
              filteredRecipes.map((recipe) => (
                <div
                  key={recipe.id}
                  onClick={() => handleSelectRecipe(recipe.id)}
                  style={{
                    padding: '1rem',
                    background: '#F5F1E8',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: '2px solid transparent',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#87A96B';
                    e.currentTarget.style.background = '#FFF9E6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.background = '#F5F1E8';
                  }}
                >
                  <h4 style={{
                    fontWeight: '600',
                    color: '#8B7355',
                    marginBottom: '0.25rem'
                  }}>
                    {recipe.title}
                  </h4>
                  <p style={{ fontSize: '0.85rem', color: '#A0826D' }}>
                    {recipe.prep_time && `⏱️ ${recipe.prep_time}`}
                    {recipe.ingredients_quebec && ` • ${recipe.ingredients_quebec.length} ingrédients`}
                  </p>
                </div>
              ))
            ) : (
              <p style={{ textAlign: 'center', color: '#A0826D', padding: '2rem' }}>
                Aucune recette trouvée
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Liste d'épicerie */}
      <Dialog open={showGroceryList} onOpenChange={setShowGroceryList}>
        <DialogContent style={{ maxWidth: '700px', maxHeight: '80vh', overflowY: 'auto' }}>
          <DialogHeader>
            <DialogTitle>🛒 Liste d'épicerie</DialogTitle>
          </DialogHeader>
          
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ color: '#A0826D', fontSize: '0.9rem' }}>
              {groceryList.filter(i => !i.checked).length} article(s) restant(s) • {groceryList.filter(i => i.checked).length} coché(s)
            </p>
          </div>

          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {/* Trier pour mettre les cochés en bas */}
            {(() => {
              const sorted = [...groceryList]
                .map((item, originalIndex) => ({ ...item, originalIndex }))
                .sort((a, b) => {
                  if (a.checked === b.checked) return a.originalIndex - b.originalIndex;
                  return a.checked ? 1 : -1;
                });
              
              const uncheckedCount = sorted.filter(i => !i.checked).length;
              const checkedCount = sorted.filter(i => i.checked).length;
              
              return sorted.map((item, idx) => (
                <React.Fragment key={item.originalIndex}>
                  {/* Séparateur entre non-cochés et cochés */}
                  {idx === uncheckedCount && checkedCount > 0 && (
                    <div style={{
                      padding: '0.5rem 0',
                      margin: '0.5rem 0',
                      borderTop: '2px dashed #D4A574',
                      textAlign: 'center',
                      color: '#A0826D',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      Articles cochés ({checkedCount})
                    </div>
                  )}
                  {/* Item */}
              <div
                key={item.originalIndex}
                style={{
                  padding: '0.75rem',
                  background: item.checked ? '#F5F1E8' : 'white',
                  border: '1px solid #D4A574',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '0.75rem',
                  textDecoration: item.checked ? 'line-through' : 'none',
                  opacity: item.checked ? 0.5 : 1,
                  transition: 'all 0.3s ease'
                }}
              >
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleGroceryItem(item.originalIndex);
                  }}
                  style={{ 
                    marginTop: '0.25rem', 
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px'
                  }}
                />
                <div 
                  style={{ flex: 1, cursor: 'pointer' }}
                  onClick={() => toggleGroceryItem(item.originalIndex)}
                >
                  <p style={{
                    color: '#3A4A3E',
                    fontWeight: '500',
                    marginBottom: '0.25rem'
                  }}>
                    {item.text}
                  </p>
                  <p style={{
                    fontSize: '0.8rem',
                    color: '#A0826D',
                    fontStyle: 'italic'
                  }}>
                    {item.recipe_title}
                  </p>
                </div>
              </div>
                </React.Fragment>
              ));
            })()}
          </div>

          {groceryList.length === 0 && (
            <p style={{ textAlign: 'center', color: '#A0826D', padding: '2rem' }}>
              Aucun ingrédient dans la liste
            </p>
          )}
        </DialogContent>
      </Dialog>
      
      <AdBanner />
      <Footer />
    </div>
  );
}
